import React, { useEffect, useMemo, useState } from "react";
import { API_BASE } from "../../api/config";
import FolgevertragModal, { modalBox, modalGrid, modalInput, modalLabel, modalPrimary } from "./FolgevertragModal";

/**
 * Welle 109: Vorgang „Neuer Mieter".
 *   1 · Einheit wählen → aktueller Vertrag, Mieter, Kündigungsstand
 *   2 · Alter Mieter   → läuft keine Kündigung, wird der Auszug hier mit angelegt
 *   3 · Neuer Mieter   → FolgevertragModal (Mietvertrag-PDF auslesen, Mieterdaten, Miete)
 * Das Backend legt alles in einem Zug an und verknüpft Auszug und Einzug.
 *
 * Props: onClose(), onFertig({einzug_id, auszug_id, mietvertrag_nr})
 */
const API = `${API_BASE}/mietverwaltung/api`;
const fmt = (s) => (s ? String(s).slice(0, 10).split("-").reverse().join(".") : "–");
const STATUS_TEXT = {
  aktiv: "nicht gekündigt", endet: "Vertragsende eingetragen, aber kein Kündigungsvorgang",
  gekuendigt: "gekündigt — Kündigungsvorgang läuft", leerstand: "Leerstand — kein aktueller Mieter",
};

export default function NeuerMieterAssistent({ onClose, onFertig }) {
  const [einheiten, setEinheiten] = useState([]);
  const [objekt, setObjekt] = useState("");
  const [einheit, setEinheit] = useState("");
  const [status, setStatus] = useState(null);
  const [auszug, setAuszug] = useState({ richtung: "mieter", eingang_am: "", kuendigung_zum: "", uebergabe_termin: "" });
  const [schritt, setSchritt] = useState(1);
  const [fehler, setFehler] = useState("");

  useEffect(() => {
    fetch(`${API}/vertraege`).then((r) => r.json()).then((d) => {
      const liste = Array.from(new Map((d.vertraege || []).filter((v) => v.einheit && /^[A-Z0-9]{2,3}-\d{2}$/.test(v.einheit))
        .map((v) => [v.einheit, { einheit: v.einheit, objekt: v.objekt, name: v.name }])).values())
        .sort((a, b) => a.einheit.localeCompare(b.einheit));
      setEinheiten(liste);
    }).catch((e) => setFehler(String(e.message || e)));
  }, []);
  const objekte = useMemo(() => Array.from(new Set(einheiten.map((e) => e.objekt))).sort(), [einheiten]);

  useEffect(() => {
    if (!einheit) { setStatus(null); return; }
    setFehler("");
    fetch(`${API}/vorgang/einheit-status?einheit=${encodeURIComponent(einheit)}`)
      .then(async (r) => { const j = await r.json(); if (!r.ok) throw new Error(j.detail || "Einheit nicht gefunden"); return j; })
      .then((j) => {
        setStatus(j);
        const v = j.vertrag || {};
        setAuszug((a) => ({ ...a, kuendigung_zum: v.kuendigung_zum || v.auszug || v.vertrag_ende || "" }));
      })
      .catch((e) => setFehler(String(e.message || e)));
  }, [einheit]);

  const brauchtAuszug = status && (status.status === "aktiv" || status.status === "endet");

  const absenden = async (nutzlast, extra) => {
    const body = {
      einheit,
      alter_vertrag: status?.vertrag?.mietvertrag_nr || null,
      auszug: brauchtAuszug ? {
        richtung: auszug.richtung,
        eingang_am: auszug.eingang_am || null,
        schreiben_datum: auszug.eingang_am || null,
        kuendigung_zum: auszug.kuendigung_zum || null,
        uebergabe_termin: auszug.uebergabe_termin || null,
      } : null,
      neuer_vertrag: nutzlast,
      uebergabe_termin_einzug: extra?.uebergabeTermin || null,
    };
    const r = await fetch(`${API}/vorgang/neuer-mieter`, { method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body) });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.detail || "Anlegen fehlgeschlagen");
    return j;
  };

  if (schritt === 3) {
    const alt = status?.vertrag;
    return (
      <FolgevertragModal einheit={einheit} einheitQm={status?.einheit?.wohnflaeche}
        titel="Neuer Mieter" mitUebergabeTermin absenden={absenden}
        hinweis={alt ? `Bisheriger Mieter: ${alt.mv_kurzname || alt.mieter1_name} (${alt.mietvertrag_nr}) — `
          + (brauchtAuszug ? "sein Auszug wird mit angelegt und verknüpft." : "der laufende Kündigungsvorgang wird verknüpft.")
          : "Einheit steht leer — es wird nur der Einzug angelegt."}
        onClose={onClose}
        onCreated={(neu) => onFertig?.({ einzug_id: neu.vorgang_id, auszug_id: neu.auszug_id, mietvertrag_nr: neu.mietvertrag_nr })} />
    );
  }

  return (
    <div className="me-drawer-overlay" style={{ display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div style={{ ...modalBox, width: "min(620px, 94vw)" }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: 0 }}>Neuer Mieter — Schritt {schritt} von 3</h3>
        <p style={{ fontSize: 13, color: "#94a3b8", margin: "6px 0 0" }}>
          Legt den Einzug des neuen Mieters an und — falls noch nicht geschehen — den Auszug des bisherigen.
          Beide Vorgänge sind verknüpft; die Dokumente (Mietvertrag, Übergabeprotokoll, Wohnungsgeber- und
          Mietbescheinigung) stehen danach im Einzug bereit.
        </p>

        {schritt === 1 && (
          <div style={modalGrid}>
            <label style={modalLabel}>Objekt
              <select style={modalInput} value={objekt} onChange={(e) => { setObjekt(e.target.value); setEinheit(""); }}>
                <option value="">— wählen —</option>
                {objekte.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </label>
            <label style={modalLabel}>Einheit
              <select style={modalInput} value={einheit} disabled={!objekt} onChange={(e) => setEinheit(e.target.value)}>
                <option value="">— wählen —</option>
                {einheiten.filter((e) => e.objekt === objekt).map((e) => (
                  <option key={e.einheit} value={e.einheit}>{e.einheit} · {e.name}</option>
                ))}
              </select>
            </label>
            {status && (
              <div style={{ gridColumn: "1 / 3", fontSize: 13.5, lineHeight: 1.6, background: "#0f172a",
                            border: "1px solid #334155", borderRadius: 8, padding: "10px 12px" }}>
                <div><b>{status.einheit.kurzname}</b> · {status.einheit.lage_einheit || ""} · {status.einheit.wohnflaeche} m²</div>
                {status.vertrag ? (
                  <>
                    <div>Aktueller Mieter: <b>{status.vertrag.mv_kurzname || status.vertrag.mieter1_name}</b>
                      {" "}({status.vertrag.mietvertrag_nr}, seit {fmt(status.vertrag.vertrag_beginn)})</div>
                    <div>Status: <b style={{ color: status.status === "gekuendigt" ? "#86efac" : "#fcd34d" }}>
                      {STATUS_TEXT[status.status]}</b>
                      {status.kuendigung?.kuendigung_zum ? ` · zum ${fmt(status.kuendigung.kuendigung_zum)}` : ""}</div>
                  </>
                ) : <div>Status: <b>{STATUS_TEXT.leerstand}</b></div>}
                {status.kuenftige_vertraege?.length > 0 && (
                  <div style={{ color: "#fcd34d" }}>Es gibt schon einen künftigen Vertrag ({status.kuenftige_vertraege.join(", ")}).
                    Ist das der neue Mieter: „Weiter“ und seinen Mietvertrag hochladen — er wird erkannt und
                    übernommen, statt ein zweites Mal angelegt zu werden.</div>
                )}
                {status.interessenten?.length > 0 && (
                  <div style={{ color: "#94a3b8" }}>Mietinteressent (nicht unterschrieben, zählt nicht als Mieter):{" "}
                    {status.interessenten.join(", ")}</div>
                )}
              </div>
            )}
          </div>
        )}

        {schritt === 2 && status && (
          <div style={{ marginTop: 12 }}>
            <p style={{ fontSize: 13.5, margin: 0 }}>
              <b>{status.vertrag.mv_kurzname || status.vertrag.mieter1_name}</b> ({status.vertrag.mietvertrag_nr}) ist noch
              nicht gekündigt. Der Auszug wird mit diesen Angaben angelegt:
            </p>
            <div style={modalGrid}>
              <label style={modalLabel}>Wer kündigt?
                <select style={modalInput} value={auszug.richtung} onChange={(e) => setAuszug({ ...auszug, richtung: e.target.value })}>
                  <option value="mieter">Mieter kündigt</option>
                  <option value="vermieter">Vermieter kündigt</option>
                </select>
              </label>
              <label style={modalLabel}>Kündigung eingegangen / Schreiben vom
                <input type="date" style={modalInput} value={auszug.eingang_am} onChange={(e) => setAuszug({ ...auszug, eingang_am: e.target.value })} />
              </label>
              <label style={modalLabel}>Vertragsende (Kündigung zum)
                <input type="date" style={modalInput} value={auszug.kuendigung_zum} onChange={(e) => setAuszug({ ...auszug, kuendigung_zum: e.target.value })} />
              </label>
              <label style={modalLabel}>Übergabetermin (Auszug)
                <input type="datetime-local" style={modalInput} value={auszug.uebergabe_termin} onChange={(e) => setAuszug({ ...auszug, uebergabe_termin: e.target.value })} />
              </label>
            </div>
            <p style={{ fontSize: 12.5, color: "#94a3b8" }}>Ohne Vertragsende rechnet das Programm die gesetzliche Frist aus.</p>
          </div>
        )}

        {fehler && <p style={{ color: "#fca5a5", fontSize: 13 }}>{fehler}</p>}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
          <button className="me-btn me-btn-outline" onClick={schritt === 1 ? onClose : () => setSchritt(1)}>
            {schritt === 1 ? "Abbrechen" : "Zurück"}</button>
          <button className="me-btn" style={modalPrimary} disabled={!status}
            onClick={() => setSchritt(schritt === 1 && brauchtAuszug ? 2 : 3)}>Weiter</button>
        </div>
      </div>
    </div>
  );
}
